import React, { useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import './Settings.scss';

export const Settings: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    marketing: false,
    updates: true,
  });
  const [language, setLanguage] = useState('en');

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your account preferences and application settings.</p>
      </div>

      <div className="settings-grid">
        {/* Appearance */}
        <div className="glass-card settings-card">
          <div className="settings-header">
            <div className="settings-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/>
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
              </svg>
            </div>
            <div>
              <h3>Appearance</h3>
              <p>Customize how FLVR SOFT looks</p>
            </div>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <h4>Dark Mode</h4>
              <p>Switch between light and dark themes</p>
            </div>
            <button 
              className={`toggle-btn ${isDark ? 'active' : ''}`}
              onClick={toggleTheme}
            >
              <div className="toggle-slider">
                {isDark ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="5"/>
                    <line x1="12" y1="1" x2="12" y2="3"/>
                    <line x1="12" y1="21" x2="12" y2="23"/>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                    <line x1="1" y1="12" x2="3" y2="12"/>
                    <line x1="21" y1="12" x2="23" y2="12"/>
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                  </svg>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Notifications */}
        <div className="glass-card settings-card">
          <div className="settings-header">
            <div className="settings-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <div>
              <h3>Notifications</h3>
              <p>Choose what you want to be notified about</p>
            </div>
          </div>
          
          {Object.entries(notifications).map(([key, value]) => (
            <div key={key} className="setting-item">
              <div className="setting-info">
                <h4>{key.charAt(0).toUpperCase() + key.slice(1)} Notifications</h4>
                <p>Receive {key} notifications</p>
              </div>
              <button 
                className={`toggle-btn ${value ? 'active' : ''}`}
                onClick={() => setNotifications({ ...notifications, [key]: !value })}
              >
                <div className="toggle-slider" />
              </button>
            </div>
          ))}
        </div>

        {/* Language */}
        <div className="glass-card settings-card">
          <div className="settings-header">
            <div className="settings-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <div>
              <h3>Language & Region</h3>
              <p>Set your preferred language</p>
            </div>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <h4>Language</h4>
              <p>Select your interface language</p>
            </div>
            <div className="select-wrapper">
              <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="pt">Português</option>
              </select>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="glass-card settings-card">
          <div className="settings-header">
            <div className="settings-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div>
              <h3>Security</h3>
              <p>Keep your account secure</p>
            </div>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <h4>Two-Factor Authentication</h4>
              <p>Add an extra layer of security</p>
            </div>
            <button className="glass-button">
              Enable
            </button>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <h4>Change Password</h4>
              <p>Update your password regularly</p>
            </div>
            <button className="glass-button">
              Update
            </button>
          </div>
        </div>

        {/* Account */}
        <div className="glass-card settings-card danger">
          <div className="settings-header">
            <div className="settings-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div>
              <h3>Account Actions</h3>
              <p>Irreversible actions for your account</p>
            </div>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <h4>Export Data</h4>
              <p>Download all your account data</p>
            </div>
            <button className="glass-button">
              Export
            </button>
          </div>
          
          <div className="setting-item">
            <div className="setting-info">
              <h4>Delete Account</h4>
              <p>Permanently delete your account</p>
            </div>
            <button className="glass-button danger">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
